import { Platform } from '@angular/cdk/platform';
import { AfterViewInit, Component, computed, DestroyRef, inject, OnDestroy, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Data, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AuthService } from '@core/auth/auth.service';
import { AuthUtils } from '@core/auth/auth.utils';
import { AppConfig } from '@core/config/app.config';
import { FuseConfigService } from '@fuse/services/config/config.service';
import { OfficeTenantsService } from '@office/tenants/office-tenants.service';
import { defaultTitle } from '@shared/constants/bringpro-constants';
import { AuthSettingSlugs } from '@shared/constants/website-settings-slugs.type';
import { EventMediaUploadStorageService } from '@shared/db/services/event-media-upload-storage.service';
import { ReadyForDeliveryToastDisplayService } from '@shared/db/services/ready-for-delivery-toast-display.service';
import { FlutterEventType } from '@shared/enums/flutter-event-type.enum';
import { OfficeRoles, Role } from '@shared/enums/roles.enum';
import { UserAvailability } from '@shared/enums/user-status.enum';
import { FirebaseMessagesService } from '@shared/firebase-messages.service';
import { FlutterEventDetail } from '@shared/models/domain/custom-views.model';
import { AlertService } from '@shared/services/alert.service';
import { ServiceWorkerService } from '@shared/services/service-worker.service';
import { WebsiteSettingsViewService } from '@shared/services/settings.service';
import { catchError, EMPTY, filter, interval, map, mergeMap, tap, timer } from 'rxjs';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [RouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
    private destroyRef = inject(DestroyRef);
    private titleService = inject(Title);
    private activatedRoute = inject(ActivatedRoute);
    private eventMediaUploadStorageService = inject(EventMediaUploadStorageService);
    private readyForDeliveryToastDisplayService = inject(ReadyForDeliveryToastDisplayService);
    private authService = inject(AuthService);
    private officeTenantsService = inject(OfficeTenantsService);
    private serviceWorker = inject(ServiceWorkerService);
    private firebaseMessagesService = inject(FirebaseMessagesService);
    private router = inject(Router);
    private platform = inject(Platform);
    private alertService = inject(AlertService);
    private fuseConfigService = inject(FuseConfigService);
    private _websiteSettingsService = inject(WebsiteSettingsViewService);
    private currentTheme: string;
    private readonly authTokenParam = new URLSearchParams(window.location.search).get('auth_token');
    private _allSettings = toSignal(this._websiteSettingsService.currentWebsiteSettings$);
    isLoading = signal(false);
    readonly logoUrl = computed(() =>
        this._allSettings()?.[AuthSettingSlugs.SubheaderLogo]?.fullUrl
        || this._websiteSettingsService.websiteSetting(AuthSettingSlugs.SubheaderLogo)?.fullUrl
        || 'assets/images/logo/bringpro-logo-guy.png'
    );

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void {
        if (this.authTokenParam) {
            this._websiteSettingsService.loadAuthSettings();
        }
        this.serviceWorker?.clearExistingBrowserCache();
        this.firebaseMessagesService.requestPermission();

        window.addEventListener('flutterData', this.handleFlutterData);

        // Subscribe to theme changes
        this.fuseConfigService.config$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap((config: AppConfig) => {
                    this.currentTheme = config.theme || 'theme-metropolitan';
                })
            )
            .subscribe();

        this.router.events
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((event) => event instanceof NavigationEnd),
                map(() => this.activatedRoute),
                map((route) => {
                    while (route.firstChild) {
                        route = route.firstChild;
                    }
                    return route;
                }),
                filter((route) => route.outlet === 'primary'),
                mergeMap((route) => route.data),
                tap(({ title }: Data) => {
                    this.titleService.setTitle(this.getThemeAwareTitle((title as string) || defaultTitle));
                })
            )
            .subscribe();

        interval(5 * 60 * 1000)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.authPingInterval());

        //repeat every 30 minutes
        interval(30 * 60 * 1000)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.validateTokenExpiration();
            });

        //repeat every 5 minutes
        timer(0, 5 * 60 * 1000)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.eventMediaUploadStorageService.checkPendingUploadsAsync();
            });

        this.checkAndRefreshToken();
        this.checkAuthTokenParameter();
        requestAnimationFrame(() => this.authPingInterval());
    }

    ngOnDestroy(): void {
        window.removeEventListener('flutterData', this.handleFlutterData);
    }

    ngAfterViewInit(): void {
        if (this.platform.IOS && window.visualViewport) {
            window.visualViewport.addEventListener('resize', () => {
                window.dispatchEvent(new Event('resize'));
            }, { passive: true });
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private authPingInterval(): void {
        if (this.authService?.userProfile?.user?.status !== UserAvailability.Invisible && this.authService?.accessToken) {
            this.authService.authPingInterval().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        }
    }

    private checkAndRefreshToken(): void {
        const token = this.authService.accessToken;
        if (token) {
            const decodedToken = AuthUtils._decodeToken(token);
            const issuedAt = decodedToken.iat * 1000;
            const oneDay = 24 * 60 * 60 * 1000;
            const now = Date.now();
            const isMissingPermissions = !!this.authService.userProfile && !this.authService.userProfile.permissions;
            if (now - issuedAt > oneDay || isMissingPermissions) this.validateTokenExpiration();
            else this.authService.postTokenToAuthChannel();
        }
    }

    /**
     * Check for auth_token query parameter and handle automatic login
     */
    private checkAuthTokenParameter(): void {
        if (!this.authService.accessToken && this.authTokenParam) {
            this.authService.accessToken = this.authTokenParam;
            this.isLoading.set(true);
            this.authService.refreshToken().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: () => {
                    this.redirectAfterLogin();
                },
                error: () => {
                    this.isLoading.set(false);
                }
            });
        } else {
            this.redirectAfterEmptyPath();
        }
    }

    private validateTokenExpiration(): void {
        this.authService.check()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                mergeMap((isLogged) => {
                    if (!isLogged) return EMPTY;
                    return this.authService.refreshToken().pipe(catchError(() => EMPTY));
                })
            )
            .subscribe();
    }

    private redirectAfterEmptyPath(): void {
        const pathname = window.location.pathname;
        const pathSegments = pathname.split('/').filter(Boolean);
        const currentSlug = this.officeTenantsService.getAllTenantsViewItems()?.find((item) => item.slug === pathSegments[0])?.slug;
        const roles = this.authService?.userProfile?.userRoles || [];
        const redirectUrlParam = new URLSearchParams(window.location.search).get('redirectUrl');
        const isTenantRoot = pathname === `/${currentSlug}` || pathname === `/${currentSlug}/`;
        const defaultRoute = roles.some((r) => r.roleName === Role.Driver)
            ? '/driver/dashboard' : roles.some((r) => r.roleName === Role.Warehouse) 
            ? '/office/warehouse-jobs/inventory' : roles.some((r) => OfficeRoles.includes(r.roleName as Role)) ? '/office/reports' : '/dashboard';

        if (redirectUrlParam && this.authService.accessToken) {
            this.router.navigateByUrl(currentSlug ? `/${currentSlug}${redirectUrlParam}` : redirectUrlParam);
        } else if (!pathSegments.length) {
            this.router.navigateByUrl(defaultRoute);
        } else if (isTenantRoot) {
            this.router.navigateByUrl(currentSlug ? `/${currentSlug}${defaultRoute}` : defaultRoute);
        }
    }

    /**
     * Get theme-aware title based on current theme
     * @param baseTitle 
     * @returns 
     */
    private getThemeAwareTitle(baseTitle: string): string {
        if (this.currentTheme === 'theme-metropolitan') {
            return baseTitle.replace(/BringPro/g, 'Metropolitan');
        }
        return baseTitle;
    }

    /**
     * Handle Flutter events
     * @param event 
     */
    private handleFlutterData = (event: CustomEvent<FlutterEventDetail>) => {
        if (event.detail.type === FlutterEventType.Navigation) {
            const path = event.detail?.path;
            if (path && typeof path === 'string') {
                this.router.navigate([path]).catch(() => {
                    this.alertService.errorToast('Error', 'Failed to navigate to the requested path. Please try again.');
                });
            }
        }
    };

    private redirectAfterLogin(): void {
        let defaultRoute = '/dashboard';
        const roles = this.authService.userProfile?.userRoles;

        if (roles?.some(r => r.roleName === Role.Driver)) {
            defaultRoute = '/driver/jobs/pending';
        }
        else if (roles?.some(r => r.roleName === Role.Warehouse)) {
            defaultRoute = '/office/warehouse-jobs/inventory';
        }
        else if (roles?.some(r => OfficeRoles.includes(r.roleName as Role))) {
            defaultRoute = '/office/reports';
        }
        requestAnimationFrame(() => {
            this.router.navigate([`${defaultRoute}`]).then(() => {
                this.isLoading.set(false);
            }).catch(() => {
                this.isLoading.set(false);
            });
        });
    }
}
